import { useCallback, useEffect, useRef } from 'react';
import { useSubscription } from '@apollo/client';
import type { ApolloError, DocumentNode, OnDataOptions } from '@apollo/client';

/**
 * GraphQL subscriptions: Apollo TERMINATES the observable when the link errors —
 * a severed socket (rolling deploy, ALB idle kill, network blip) ends the
 * operation for good. With `lazy: true` the graphql-ws client then has nothing
 * left to reconnect for, so its own retry budget is never spent and live
 * delivery stays dead until the member reloads the page. Restarting the
 * GraphQL subscription is what puts an active operation back, which is what
 * lets the socket layer reconnect at all.
 */
const RESUBSCRIBE_BASE_MS = 1_000;
const RESUBSCRIBE_MAX_MS = 30_000;

export function useResilientGraphQLSubscription<TData = any>(
  document: DocumentNode,
  options: {
    skip?: boolean;
    label: string;
    onData?: (result: OnDataOptions<TData>) => void;
    onLost?: (attempt: number, error: ApolloError) => void;
    onRecovered?: (attempts: number) => void;
  }
) {
  const { skip = false, label, onData, onLost, onRecovered } = options;

  const attemptsRef = useRef(0);
  const lastErrorRef = useRef(0);
  const lostSentRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restartRef = useRef<(() => void) | null>(null);
  const activeRef = useRef(!skip);
  activeRef.current = !skip;

  const labelRef = useRef(label);
  labelRef.current = label;
  const onDataRef = useRef(onData);
  onDataRef.current = onData;
  const onLostRef = useRef(onLost);
  onLostRef.current = onLost;
  const onRecoveredRef = useRef(onRecovered);
  onRecoveredRef.current = onRecovered;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimer, [clearTimer]);
  useEffect(() => {
    if (skip) {
      clearTimer();
      attemptsRef.current = 0;
      lostSentRef.current = false;
    }
  }, [skip, clearTimer]);

  const { restart } = useSubscription<TData>(document, {
    skip,
    onData: (result) => {
      const recovered = attemptsRef.current;
      if (recovered > 0) {
        attemptsRef.current = 0;
        onRecoveredRef.current?.(recovered);
      }
      lostSentRef.current = false;
      clearTimer();
      onDataRef.current?.(result);
    },
    onError: (error) => {
      if (!activeRef.current || timerRef.current) return;

      // shortcut: a quiet gap is used as a proxy for "reconnected"; upgrade to the
      // graphql-ws connected event if outage counts need to be exact.
      const now = Date.now();
      if (now - lastErrorRef.current > 2 * RESUBSCRIBE_MAX_MS) attemptsRef.current = 0;
      lastErrorRef.current = now;

      const attempt = (attemptsRef.current += 1);
      // warn, not debug: browsers hide debug by default, which is how a dead
      // GraphQL subscription stayed invisible.
      console.warn(`${labelRef.current} GraphQL subscription error; resubscribing`, { attempt, error });
      // Quiet-gap resets attempts; lost stays latched until data or skip.
      if (!lostSentRef.current) {
        lostSentRef.current = true;
        onLostRef.current?.(attempt, error);
      }

      const wait = Math.min(RESUBSCRIBE_MAX_MS, RESUBSCRIBE_BASE_MS * 2 ** (attempt - 1));
      timerRef.current = setTimeout(
        () => {
          timerRef.current = null;
          if (!activeRef.current) return;
          restartRef.current?.();
        },
        wait + Math.floor(Math.random() * 500)
      );
    },
  });

  restartRef.current = restart;
}
